import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Mail, Send, RefreshCcw, CheckCircle, XCircle, Clock, 
  ChevronLeft, ChevronRight, AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import AdminLayout from '@/components/admin/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AdminEmailDelivery() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [resendingId, setResendingId] = useState(null);
  const [confirmResend, setConfirmResend] = useState(null);

  useEffect(() => {
    loadLogs();
  }, [page, statusFilter]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API_URL}/api/admin/email-delivery-logs?page=${page}&limit=50&status=${statusFilter}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLogs(res.data.logs || []);
      setTotalPages(res.data.pages || 1);
    } catch (error) {
      console.error('Failed to load email logs:', error);
      toast.error('Failed to load email delivery logs');
    } finally {
      setLoading(false);
    }
  };

  const resendEmail = async (orderId) => {
    try {
      setResendingId(orderId);
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/admin/purchases/${orderId}/resend-delivery-email`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Delivery email resent successfully');
      setConfirmResend(null);
      loadLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend email');
    } finally {
      setResendingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      'sent': { label: 'Sent', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
      'delivered': { label: 'Delivered', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
      'failed': { label: 'Failed', color: 'bg-red-500/20 text-red-400', icon: XCircle },
      'pending': { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
      'retrying': { label: 'Retrying', color: 'bg-blue-500/20 text-blue-400', icon: RefreshCcw },
      'retried': { label: 'Retried', color: 'bg-[#00F0FF]/20 text-[#00F0FF]', icon: RefreshCcw },
    };
    return badges[status] || { label: status || 'Unknown', color: 'bg-neutral-500/20 text-neutral-400', icon: Clock };
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
              <Mail className="w-8 h-8 text-[#00F0FF]" />
              Email Delivery
            </h1>
            <p className="text-neutral-400 mt-1">Monitor and resend product delivery emails</p>
          </div>
          
          <Button
            onClick={loadLogs}
            variant="outline"
            className="border-white/20"
          >
            <RefreshCcw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Status Filter */}
        <div className="flex flex-wrap gap-2">
          {['all', 'sent', 'failed', 'pending', 'retrying'].map(status => (
            <Button
              key={status}
              size="sm"
              variant={statusFilter === status ? 'default' : 'outline'}
              onClick={() => { setStatusFilter(status); setPage(1); }}
              className={statusFilter === status ? 'bg-[#00F0FF] text-black' : 'border-white/20'}
            >
              {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00F0FF]"></div>
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-neutral-400">
            <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No email delivery logs found</p>
          </div>
        ) : (
          <>
            <div className="bg-neutral-900/50 border border-white/10 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-black/30">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-neutral-400">Order</th>
                      <th className="text-left p-4 text-sm font-medium text-neutral-400">Recipient</th>
                      <th className="text-left p-4 text-sm font-medium text-neutral-400">Template</th>
                      <th className="text-left p-4 text-sm font-medium text-neutral-400">Status</th>
                      <th className="text-left p-4 text-sm font-medium text-neutral-400">Attempts</th>
                      <th className="text-left p-4 text-sm font-medium text-neutral-400">Time</th>
                      <th className="text-left p-4 text-sm font-medium text-neutral-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {logs.map((log, idx) => {
                      const statusBadge = getStatusBadge(log.status);
                      const StatusIcon = statusBadge.icon;
                      return (
                        <tr key={log._id || log.log_id || log.order_id || log.purchase_id || `log-${log.created_at || idx}`} className="hover:bg-white/5">
                          <td className="p-4 font-mono text-sm">
                            {(log.order_id || log.purchase_id)?.slice(0, 16)}...
                          </td>
                          <td className="p-4 text-sm">
                            {log.recipient_email || log.email || 'N/A'}
                          </td>
                          <td className="p-4 text-sm text-neutral-400">
                            {log.template_type || log.email_type || 'delivery'}
                          </td>
                          <td className="p-4">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${statusBadge.color}`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusBadge.label}
                            </span>
                          </td>
                          <td className="p-4 text-sm text-center">
                            {log.attempts || log.retry_count || 1}
                          </td>
                          <td className="p-4 text-sm text-neutral-400">
                            {formatDate(log.created_at || log.sent_at)}
                          </td>
                          <td className="p-4">
                            {(log.status === 'failed' || log.status === 'pending') && (
                              <Button
                                size="sm"
                                onClick={() => setConfirmResend(log)}
                                disabled={resendingId === (log.order_id || log.purchase_id)}
                                className="bg-white text-black hover:bg-[#00F0FF]/90"
                              >
                                {resendingId === (log.order_id || log.purchase_id) ? (
                                  <RefreshCcw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Send className="w-4 h-4 mr-1" />
                                    Resend
                                  </>
                                )}
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Error Details */}
            {logs.some(l => l.last_error || l.error_message) && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  <span className="font-semibold text-red-400">Recent Errors</span>
                </div>
                <div className="space-y-2">
                  {logs.filter(l => l.last_error || l.error_message).slice(0, 3).map((log, idx) => (
                    <div key={`err-${log._id || log.order_id || log.purchase_id || idx}`} className="text-sm text-neutral-300 bg-black/30 p-2 rounded">
                      <span className="font-mono text-xs text-neutral-400">
                        {(log.order_id || log.purchase_id)?.slice(0, 12)}:
                      </span>{' '}
                      {log.last_error || log.error_message}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                  className="border-white/20"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="px-4 py-2 text-sm">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === totalPages}
                  onClick={() => setPage(p => p + 1)}
                  className="border-white/20"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </>
        )}

        {/* Confirm Resend Dialog */}
        <Dialog open={!!confirmResend} onOpenChange={() => setConfirmResend(null)}>
          <DialogContent className="bg-neutral-900 border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-white" />
                Confirm Resend
              </DialogTitle>
              <DialogDescription className="text-neutral-400">
                Are you sure you want to resend the delivery email?
              </DialogDescription>
            </DialogHeader>
            
            {confirmResend && (
              <div className="space-y-4">
                <div className="bg-black/30 p-3 rounded text-sm">
                  <div className="text-neutral-400">Recipient</div>
                  <div>{confirmResend.recipient_email || confirmResend.email}</div>
                </div>
                <div className="bg-black/30 p-3 rounded text-sm">
                  <div className="text-neutral-400">Order ID</div>
                  <div className="font-mono">{confirmResend.order_id || confirmResend.purchase_id}</div>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    onClick={() => resendEmail(confirmResend.order_id || confirmResend.purchase_id)}
                    disabled={resendingId}
                    className="flex-1 bg-white text-black hover:bg-[#00F0FF]/90"
                  >
                    {resendingId ? (
                      <RefreshCcw className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Resend Email
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setConfirmResend(null)}
                    className="flex-1 border-white/20"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}

export default AdminEmailDelivery;
